import React, {useState} from 'react';
import {Save, CheckCircle, Heart, Lightbulb, Users} from 'lucide-react';
import preparatoryApiService from "../../api/preparatoryApiService";

/**
 * Preparatory stage self-assessment component with modern UI design
 * Matches the style of LessonPlanHistory and AssessmentManagementPanel
 */
const PreparatorySelfAssessment = ({
                                       assignmentId,
                                       submission,
                                       onSubmitComplete
                                   }) => {
    // Initialize state for form data with all the questions from the image
    const [selfAssessmentForm, setSelfAssessmentForm] = useState({
        followed_instructions: "Not sure",
        liked_work: "Not sure",
        asked_for_help: "Not sure",
        tried_my_best: "Not sure",
        proud_of_work: "Not sure",
        want_to_repeat: "Not sure",
        liked_groupwork: "Not sure",
        asked_classmate_help: "Not sure",
        needed: []
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Define questions for better organization
    const questions = [
        { key: 'followed_instructions', text: 'I followed my teacher\'s instructions.' },
        { key: 'liked_work', text: 'I liked doing this work.' },
        { key: 'asked_for_help', text: 'I asked for help if I didn\'t understand.' },
        { key: 'tried_my_best', text: 'I tried my best in this task.' },
        { key: 'proud_of_work', text: 'I am proud of my work.' },
        { key: 'want_to_repeat', text: 'I want to do this task again.' },
        { key: 'liked_groupwork', text: 'I liked working with my classmate/s.' },
        { key: 'asked_classmate_help', text: 'I could ask my classmates for help, and they helped me.' }
    ];

    // Map UI values to API values
    const mapValueToResponse = (value) => {
        if (value === true) return "Yes";
        if (value === false) return "No";
        return "Not sure"; // Default/null case
    };

    // Handle changes to radio button fields
    const handleSelfAssessmentChange = (field, value) => {
        setSelfAssessmentForm(prev => ({
            ...prev,
            [field]: mapValueToResponse(value)
        }));
    };

    // Submit self-assessment
    const handleSelfAssessmentSubmit = async () => {
        try {
            setIsSubmitting(true);
            setError(null);

            // Extract only the fields needed for the API
            const apiData = {
                followed_instructions: selfAssessmentForm.followed_instructions,
                liked_work: selfAssessmentForm.liked_work,
                asked_for_help: selfAssessmentForm.asked_for_help,
                tried_my_best: selfAssessmentForm.tried_my_best,
                proud_of_work: selfAssessmentForm.proud_of_work,
                want_to_repeat: selfAssessmentForm.want_to_repeat,
                liked_groupwork: selfAssessmentForm.liked_groupwork,
                asked_classmate_help: selfAssessmentForm.asked_classmate_help,
                needed: selfAssessmentForm.needed.length > 0 ? selfAssessmentForm.needed : ["none"]
            };

            const response = await preparatoryApiService.addSelfAssessment(assignmentId, apiData);

            if (onSubmitComplete) {
                onSubmitComplete(response);
            }
        } catch (err) {
            // Handle detailed validation errors
            if (typeof err.message === 'string' && err.message.includes('[object Object]')) {
                setError("Validation error: Please fill in all required fields.");
            } else {
                setError(err.message || "Failed to submit self-assessment");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get response styling
    const getResponseStyling = (currentResponse, targetResponse) => {
        if (currentResponse === targetResponse) {
            switch (targetResponse) {
                case "Yes":
                    return 'bg-green-100 border-2 border-green-500 scale-110';
                case "No":
                    return 'bg-red-100 border-2 border-red-500 scale-110';
                case "Not sure":
                    return 'bg-yellow-100 border-2 border-yellow-500 scale-110';
                default:
                    return 'bg-gray-100';
            }
        }
        return 'bg-gray-100';
    };

    // If already submitted, show the submitted assessment
    if (submission && submission.self_assessment) {
        return (
            <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                            <CheckCircle className="w-6 h-6 text-white"/>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Self-Assessment Complete</h2>
                            <p className="text-gray-600 mt-1">Your reflection has been submitted successfully</p>
                        </div>
                    </div>
                </div>

                <div className="p-8">
                    <div className="bg-blue-50 rounded-xl p-4 mb-8 border border-blue-200">
                        <p className="text-sm font-medium text-blue-800 mb-2">Assessment Results</p>
                        <p className="text-sm text-blue-700">
                            Here are your responses to the self-assessment questions.
                        </p>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Statement</th>
                                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">Response</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {questions.map((question, index) => (
                                    <tr key={question.key} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {index + 1}. {question.text}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center">
                                                <div className="flex items-center gap-4">
                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${getResponseStyling(submission.self_assessment[question.key], "Yes")}`}>
                                                            <span className="text-2xl">😊</span>
                                                        </div>
                                                        <span className="mt-1 text-xs font-medium text-gray-700">Yes</span>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${getResponseStyling(submission.self_assessment[question.key], "No")}`}>
                                                            <span className="text-2xl">😔</span>
                                                        </div>
                                                        <span className="mt-1 text-xs font-medium text-gray-700">No</span>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all ${getResponseStyling(submission.self_assessment[question.key], "Not sure")}`}>
                                                            <span className="text-2xl">🤔</span>
                                                        </div>
                                                        <span className="mt-1 text-xs font-medium text-gray-700">Not sure</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // Otherwise, show the assessment form
    return (
        <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                        <CheckCircle className="w-6 h-6 text-white"/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Self-Assessment</h2>
                        <p className="text-gray-600 mt-1">Please answer the following regarding the activity you just completed</p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <div className="bg-blue-50 rounded-xl p-4 mb-8 border border-blue-200">
                    <p className="text-sm font-medium text-blue-800 mb-2">Instructions</p>
                    <p className="text-sm text-blue-700">
                        Choose the emoji that best represents your response to each statement.
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Statement</th>
                                <th className="px-6 py-4 text-center text-sm font-medium text-gray-900">Your Response</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {questions.map((question, index) => (
                                <tr key={question.key} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {index + 1}. {question.text}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-center">
                                            <div className="flex items-center gap-4">
                                                <div className="flex flex-col items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelfAssessmentChange(question.key, true)}
                                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all hover:scale-105 ${selfAssessmentForm[question.key] === "Yes" ? 'bg-green-100 border-2 border-green-500 scale-110' : 'bg-gray-100 hover:bg-green-50'}`}
                                                    >
                                                        <span className="text-2xl">😊</span>
                                                    </button>
                                                    <span className="mt-1 text-xs font-medium text-gray-700">Yes</span>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelfAssessmentChange(question.key, false)}
                                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all hover:scale-105 ${selfAssessmentForm[question.key] === "No" ? 'bg-red-100 border-2 border-red-500 scale-110' : 'bg-gray-100 hover:bg-red-50'}`}
                                                    >
                                                        <span className="text-2xl">😔</span>
                                                    </button>
                                                    <span className="mt-1 text-xs font-medium text-gray-700">No</span>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelfAssessmentChange(question.key, null)}
                                                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-all hover:scale-105 ${selfAssessmentForm[question.key] === "Not sure" ? 'bg-yellow-100 border-2 border-yellow-500 scale-110' : 'bg-gray-100 hover:bg-yellow-50'}`}
                                                    >
                                                        <span className="text-2xl">🤔</span>
                                                    </button>
                                                    <span className="mt-1 text-xs font-medium text-gray-700">Not sure</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Submit button */}
                <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={handleSelfAssessmentSubmit}
                        disabled={isSubmitting}
                        className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={20} className="mr-3"/>
                                Save Self-Assessment
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PreparatorySelfAssessment;